/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  getAnnouncementState,
  markAnnouncementDone,
  recordAnnouncementImpression,
  snoozeAnnouncement,
} from '../lib/api'
import { WhatsNewContext } from './useWhatsNew'

interface AnnouncementContextType {
  showDialog: boolean
  /** Admin-configured survey link; null → modal uses its built-in default. */
  surveyUrl: string | null
  /** The user's existing star rating for this round (null = not rated yet), so
   * the modal can re-open already showing their rating instead of re-asking. */
  npsRating: number | null
  openDialog: () => void
  /** Close + mark done for this round. `source`: 'done' or 'take_survey' (funnel). */
  markDone: (source?: 'done' | 'take_survey') => void
  /** Close + snooze (~a week, capped). `source`: 'maybe_later' or 'close_x' (funnel). */
  snooze: (source?: 'maybe_later' | 'close_x') => void
}

const AnnouncementContext = createContext<AnnouncementContextType | null>(null)

// Min interval between GET /announcement re-checks on navigation. Bounds a
// navigation-heavy session to ~1 call/minute while still surfacing a fresh
// admin broadcast within roughly a minute.
const ANNOUNCEMENT_RECHECK_MS = 60_000

export function AnnouncementProvider({ children }: { children: ReactNode }) {
  const [showDialog, setShowDialog] = useState(false)
  const [surveyUrl, setSurveyUrl] = useState<string | null>(null)
  const [npsRating, setNpsRating] = useState<number | null>(null)
  // Set when the modal WANTS to auto-show (the gate said show=true, or a bell
  // deep-link requested it) but it may need to wait its turn behind the What's
  // New modal — see the show effect below.
  const [pendingShow, setPendingShow] = useState(false)
  const hasShownRef = useRef(false)
  // Wall-clock of the last GET /announcement so rapid navigation doesn't
  // re-poll on every route change (see ANNOUNCEMENT_RECHECK_MS).
  const lastCheckedRef = useRef(0)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Coordinate with the What's New modal so the two never stack. Announcement
  // is nested inside WhatsNewProvider (App.tsx), so we read its context here.
  // Read via useContext (not useWhatsNew) so AnnouncementProvider still works
  // without a WhatsNewProvider in the tree (e.g. unit tests): a missing
  // provider reads as "resolved, nothing showing" → no behavior change.
  const whatsNew = useContext(WhatsNewContext)
  const whatsNewResolved = whatsNew?.hasResolved ?? true
  const whatsNewOpen = whatsNew?.showDialog ?? false

  // Re-check on navigation (until shown once this session) so the modal opens
  // on whatever page the user is on — not just at initial load. This catches
  // mid-session state changes (an admin broadcast, or the self-test "Send to
  // myself" preview) without a hard reload. The workspace page renders without
  // <Layout>, so AnnouncementDialog isn't mounted there → it never shows in the
  // workspace, only on standard pages. Non-nagging: hasShownRef stops the
  // re-checks once it has auto-shown (or been opened) this session. Throttled
  // to ANNOUNCEMENT_RECHECK_MS so a navigation-heavy session makes at most one
  // call/minute instead of one per route change — still catches a broadcast
  // within ~a minute. Sets pendingShow rather than opening directly, so the
  // show effect can sequence it behind What's New.
  useEffect(() => {
    if (hasShownRef.current) return
    const now = Date.now()
    if (now - lastCheckedRef.current < ANNOUNCEMENT_RECHECK_MS) return
    lastCheckedRef.current = now
    getAnnouncementState()
      .then((state) => {
        setSurveyUrl(state.survey_url ?? null)
        setNpsRating(state.nps_rating ?? null)
        if (state.show && !hasShownRef.current) setPendingShow(true)
      })
      .catch(() => {
        // Silently fail — the announcement is non-critical.
      })
  }, [location.pathname])

  // Actually open the modal once it's our turn: a show is pending, we haven't
  // shown yet this session, and What's New has resolved its own initial load
  // and isn't on screen. This is the single gate that guarantees the two
  // global modals never overlap — the survey waits until What's New is done
  // (closed or never-shown), then opens. Re-runs when What's New closes.
  useEffect(() => {
    if (!pendingShow || hasShownRef.current) return
    if (!whatsNewResolved || whatsNewOpen) return
    hasShownRef.current = true
    setPendingShow(false)
    setShowDialog(true)
    // Record the 'seen' funnel event (the modal is now actually open).
    recordAnnouncementImpression().catch(() => {})
  }, [pendingShow, whatsNewResolved, whatsNewOpen])

  // Open the modal unconditionally — used by the admin "Send to myself" preview
  // and the bell-notification click (both are explicit user actions taken while
  // What's New is not on screen, so they bypass the pending-show queue).
  // Deliberately independent of the viewer's own seen/done/snooze state AND of
  // the throttled background re-check, so the admin always sees the modal right
  // after a self-send (a self-test is a preview; it shouldn't care whether the
  // admin already took the survey). Refresh the survey URL first so the CTA
  // reflects the latest admin-configured link.
  const openDialog = useCallback(() => {
    getAnnouncementState()
      .then((state) => {
        setSurveyUrl(state.survey_url ?? null)
        setNpsRating(state.nps_rating ?? null)
      })
      .catch(() => {})
    hasShownRef.current = true
    setPendingShow(false)
    setShowDialog(true)
    recordAnnouncementImpression().catch(() => {})
  }, [])

  const markDone = useCallback((source: 'done' | 'take_survey' = 'done') => {
    setShowDialog(false)
    markAnnouncementDone(source).catch(() => {
      // Silently fail — non-critical.
    })
  }, [])

  const snooze = useCallback((source: 'maybe_later' | 'close_x' = 'maybe_later') => {
    setShowDialog(false)
    snoozeAnnouncement(source).catch(() => {
      // Silently fail — non-critical.
    })
  }, [])

  // A '/?announcement=survey' deep-link (e.g. landing from an emailed/Slack
  // link) requests the modal. Route it through pendingShow so it still waits
  // behind What's New instead of stacking, and strip the param so a refresh /
  // back-nav doesn't re-trigger. (The in-app bell click opens the modal
  // directly via openDialog and doesn't use this path.)
  useEffect(() => {
    if (searchParams.get('announcement') === 'survey') {
      if (!hasShownRef.current) setPendingShow(true)
      const next = new URLSearchParams(searchParams)
      next.delete('announcement')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams])

  return (
    <AnnouncementContext.Provider value={{ showDialog, surveyUrl, npsRating, openDialog, markDone, snooze }}>
      {children}
    </AnnouncementContext.Provider>
  )
}

export function useAnnouncement() {
  const ctx = useContext(AnnouncementContext)
  if (!ctx) {
    throw new Error('useAnnouncement must be used within AnnouncementProvider')
  }
  return ctx
}
